/**
 * Documentos que chegam depois do cadastro (E84.F1.T06).
 *
 * E107.F1 (ADR-0087): também o pacote ZIP do banco, aberto no servidor, e as duas evidências
 * dele (manifesto e carimbo do tempo) — a linha do envio diz o que entrou e o que ficou de fora.
 *
 * E133.F2 (GUS-106, ADR-0089): o complemento é um recebimento, como o envio da abertura; a linha
 * que falha oferece "Tentar novamente" no mesmo recebimento, sem duplicar, e o que o exame não lê
 * fica guardado e marcado. E135.F2.C01–C03: o recibo que não fechou, desta tela ou do recibo
 * gravado (página recarregada), vem antes das linhas do envio; o recibo não lido tem aviso próprio.
 *
 * Aceita peças e comprovante enquanto nenhuma peça foi concluída. O envio é o MESMO do passo 2 do
 * cadastro (`kind='contested'`), e o servidor decide o que o arquivo é: comprovante reconhecido
 * pelo conteúdo, ou peça reconciliada contra os códigos já persistidos. Ao terminar, quem chamou
 * recarrega a lista, e a peça nova aparece com código e tipo se o comprovante já casou.
 *
 * E177.F6 (GUS-253, ADR-0130): com outras CCBs na mesma pasta e processo, "A quais CCBs estes
 * documentos se referem?" — o lote vai também às CCBs marcadas, depois de selado.
 */
import { useRef, useState } from 'react';

import { ReciboNaoLido, ReciboNaoSelado } from '@/components/CaseWizard/LinhasDoEnvio';
import { Blueprint, MonoData } from '@/components/ui';
import { FileDropzone } from '@/components/ui/FileDropzone';
import { ptBR } from '@/i18n/pt-BR';
import { useApiClient } from '@/lib/api';
import type { DocumentErrorReason } from '@/lib/case_source_documents';
import { MAX_BYTES_DO_ARQUIVO } from '@/lib/recebimentos';
import { useEnvioPorRecebimento, type LinhaDoEnvio } from '@/lib/useEnvioPorRecebimento';
import { useRecebimentosAbertos } from '@/lib/useRecebimentosAbertos';

import { DestinoPorCcb, EncaminhamentosPorCcb, useDestinosPorCcb } from './DestinoPorCcb';
import { ACCEPT_ELETRONICA, eEntradaDePacote, textoDaEntrada } from './pacote_do_banco';

const t = ptBR.exameEletronico.adicionarDocumentos;
const tw = ptBR.wizard;

function mensagemDaRecusa(nome: string, reason: DocumentErrorReason): string {
  if (reason === 'file_too_large') return tw.errorFileTooLarge;
  if (reason === 'timeout') return tw.errorTimeout.replace('{nome}', nome);
  if (reason === 'network') return tw.errorNetwork;
  if (reason === 'recebimento_not_found' || reason === 'recebimento_concluido') {
    return tw.errorRecebimento;
  }
  return t.erroGenerico.replace('{nome}', nome);
}

/** O que a linha de cada arquivo diz: em voo, o que entrou, ou a recusa. */
function textoDaLinha(linha: LinhaDoEnvio): string {
  if (linha.estado === 'enviando') return t.enviando.replace('{nome}', linha.fileName);
  if (linha.estado === 'falhou') return mensagemDaRecusa(linha.fileName, linha.erro ?? 'unknown');
  return linha.entrada === undefined
    ? t.enviado.replace('{nome}', linha.fileName)
    : textoDaEntrada(linha.fileName, linha.entrada);
}

function estadoDaLinha(linha: LinhaDoEnvio): string {
  if (linha.estado === 'enviando') return 'enviando';
  if (linha.estado === 'falhou' || linha.entrada === undefined) return 'erro';
  const { entrada } = linha;
  if (eEntradaDePacote(entrada)) return 'pacote';
  return (
    entrada.documentos[0]?.kind ?? (entrada.nao_examinados.length > 0 ? 'nao_examinado' : 'copia')
  );
}

export interface AdicionarDocumentosProps {
  caseId: string;
  /** Bloqueado depois que o perito concluiu alguma peça (ADR-0077 §2.4). */
  concluido: boolean;
  /** Chamado uma vez ao fim do lote, com sucesso em ao menos um arquivo. */
  onAdicionado: () => void;
  /** E91.F2 (GUS-51 RF03): o `id` do título da seção recolhida; o corpo não repete o seu. */
  tituloId?: string;
}

export function AdicionarDocumentos({
  caseId,
  concluido,
  onAdicionado,
  tituloId,
}: AdicionarDocumentosProps): JSX.Element {
  const client = useApiClient();
  const [emVoo, setEmVoo] = useState(false);
  const [tamanhoRecusado, setTamanhoRecusado] = useState(false);
  // Algum documento entrou (ou pode ter entrado, no tempo esgotado): a mesa relê ao fim do lote.
  const algumEntrou = useRef(false);
  const recibo = useRecebimentosAbertos(client, caseId);
  const destinos = useDestinosPorCcb(client, caseId, !concluido);
  const envio = useEnvioPorRecebimento({
    client,
    caseId,
    kind: 'contested',
    onLote: destinos.onLote,
    onAberto: destinos.onAberto,
    onConcluido: (recebimentoId) => void destinos.encaminhar(recebimentoId),
    onEntrada: (entrada) => {
      algumEntrou.current = algumEntrou.current || entrada.documentos.length > 0;
    },
    onTalvezRecebido: () => {
      algumEntrou.current = true;
    },
    abertosNoRecibo: recibo.abertos,
  });

  async function enviar(arquivos: File[]): Promise<void> {
    if (arquivos.length === 0 || emVoo) return;
    setEmVoo(true);
    setTamanhoRecusado(false);
    destinos.iniciarLote();
    algumEntrou.current = false;
    await envio.enviarLote(arquivos);
    setEmVoo(false);
    if (algumEntrou.current) onAdicionado();
  }

  async function tentarDeNovo(localId: string): Promise<void> {
    algumEntrou.current = false;
    await envio.tentarDeNovo(localId);
    if (algumEntrou.current) onAdicionado();
  }

  return (
    <Blueprint
      as="section"
      className={tituloId === undefined ? 'mt-sc-6 space-y-sc-4 p-sc-6' : 'space-y-sc-4 p-sc-4'}
      data-testid="adicionar-documentos"
      aria-labelledby={tituloId ?? 'adicionar-documentos-titulo'}
    >
      <header>
        {tituloId === undefined ? (
          <h2 id="adicionar-documentos-titulo" className="text-display-sm text-fg">
            {t.titulo}
          </h2>
        ) : null}
      </header>
      {concluido ? (
        <p className="text-body-sm text-fg-muted" data-testid="adicionar-documentos-concluido">
          {t.concluido}
        </p>
      ) : (
        <>
          <DestinoPorCcb destinos={destinos} disabled={emVoo} />
          <FileDropzone
            data-testid="adicionar-documentos-dropzone"
            inputTestId="adicionar-documentos-input"
            inputLabel={t.inputLabel}
            accept={ACCEPT_ELETRONICA}
            multiple
            maxSize={MAX_BYTES_DO_ARQUIVO}
            disabled={emVoo}
            title={t.dropzoneTitulo}
            hint={t.dropzoneHint}
            onFilesSelected={(arquivos) => void enviar(arquivos)}
            onSizeRejected={() => setTamanhoRecusado(true)}
            className="min-h-[120px]"
          />
        </>
      )}
      {tamanhoRecusado ? (
        <p role="alert" className="text-body-sm text-danger-700">
          {tw.dropzoneTooLarge}
        </p>
      ) : null}
      {recibo.naoLido ? <ReciboNaoLido onLerDeNovo={recibo.lerDeNovo} /> : null}
      {envio.linhas.length + envio.recibosNaoSelados.length > 0 ? (
        <ul className="space-y-sc-1" data-testid="adicionar-documentos-envios">
          {envio.recibosNaoSelados.map((id) => (
            <ReciboNaoSelado
              key={id}
              recebimentoId={id}
              onConcluirDeNovo={() => void envio.concluirDeNovo(id)}
            />
          ))}
          {envio.linhas.map((linha) => (
            <li
              key={linha.localId}
              className="text-body-sm text-fg-muted"
              data-testid="adicionar-documentos-envio"
              data-estado={estadoDaLinha(linha)}
            >
              <MonoData className="text-fg">{linha.fileName}</MonoData> · {textoDaLinha(linha)}
              {linha.estado === 'falhou' ? (
                <span className="ml-2 inline-flex gap-2">
                  {linha.retentavel ? (
                    <button
                      type="button"
                      data-testid="adicionar-documentos-retry"
                      onClick={() => void tentarDeNovo(linha.localId)}
                      className="text-caption text-accent-700 underline-offset-4 hover:underline"
                    >
                      {tw.actionRetry}
                    </button>
                  ) : null}
                  <button
                    type="button"
                    data-testid="adicionar-documentos-remove"
                    onClick={() => envio.removerLinha(linha.localId)}
                    className="text-caption text-fg-dim underline-offset-4 hover:underline"
                  >
                    {tw.actionRemove}
                  </button>
                </span>
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}
      <EncaminhamentosPorCcb destinos={destinos} />
    </Blueprint>
  );
}
